import { FC, ReactNode } from 'react';
import { Checkbox, Flex, Table, Tbody, Th, Thead, Tr } from '@chakra-ui/react';
import { isEmpty } from 'ramda';

import { FileBrowserStorageObjectFragment } from '../../graphql/generated';
import { SelectedFilesState } from '../../pages/file-browser-page/use-selected-files';
import { FileBrowserRow } from './file-browser-row';

export type FileBrowserProps = {
  EmptyState: ReactNode;
  objects: FileBrowserStorageObjectFragment[];
  onDownload: (file: FileBrowserStorageObjectFragment) => void;
  isShowingMultiSelect?: boolean;
  selectedFiles?: SelectedFilesState;
  toggleFileSelection?: (file: FileBrowserStorageObjectFragment) => void;
  selectAllFiles?: () => void;
  clearAllFiles?: () => void;
};

export const Placeholder: FC = ({ ...props }) => <span {...props}>--</span>;

export const FileBrowser: FC<FileBrowserProps> = ({
  objects,
  onDownload,
  EmptyState,
  isShowingMultiSelect = false,
  selectedFiles = {},
  toggleFileSelection,
  selectAllFiles,
  clearAllFiles,
  ...rest
}) => {
  const handleDownload = (
    file: FileBrowserStorageObjectFragment
  ) => async () => {
    onDownload(file);
  };

  const selectedFilesLength = Object.keys(selectedFiles).length;
  const hasSelectedAll = selectedFilesLength === objects.length;

  return (
    <>
      <Flex
        flex={1}
        direction="column"
        mt={isShowingMultiSelect ? 2 : 8}
        {...rest}
      >
        <Table variant="file-browser">
          <Thead>
            <Tr>
              {isShowingMultiSelect && (
                <Th px={4} width="60px" textAlign="right">
                  <Checkbox
                    p={2}
                    isChecked={hasSelectedAll}
                    isIndeterminate={selectedFilesLength > 0 && !hasSelectedAll}
                    onChange={() => {
                      if (selectedFilesLength > 0) {
                        clearAllFiles?.();
                      } else {
                        selectAllFiles?.();
                      }
                    }}
                    colorScheme="blue"
                    size="lg"
                    aria-label="Select all files"
                  />
                </Th>
              )}
              <Th colSpan={4}>Name</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Size</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody data-testid="file-browser-body">
            {objects.map((object) => (
              <FileBrowserRow
                key={object.id}
                storageObject={object}
                data-testid="file-browser-row"
                onDownload={handleDownload(object)}
                isSelectable={isShowingMultiSelect}
                isSelected={Boolean(selectedFiles[object.id])}
                onToggleSelection={() => toggleFileSelection?.(object)}
              />
            ))}
          </Tbody>
        </Table>
        <br />
        {isEmpty(objects) && EmptyState}
      </Flex>
    </>
  );
};
